// Importing React and external libs
import React, { useEffect, useRef, useState } from 'react';

// Importing antd components
import { Button, Checkbox, Input, message, Select, Form } from 'antd';
import 'antd/es/button/style/css';
import 'antd/es/checkbox/style/css';
import 'antd/es/input/style/css';
import 'antd/es/message/style/css';
import 'antd/es/select/style/css';

// Importing Helix hooks
import useLanguage from 'helix-hooks/language';

// Importing component languages
import languages from '../lng';

// Importing component style
import './style.scss';

// Constants
const { Option } = Select;
const AUTH_APP = 'authenticator_app';
const MAX_MFA_CODE_INPUT = 6;
const EMAIL = 'email';


const MFAVerification = (props) => {
  const lastLoginAttemptResponseRef = useRef(null);

  const { defaultMFAMethod = AUTH_APP, loginAttemptResponse, onSendCode, onVerifyCode } = props;
  const Language = useLanguage({ languages });

  const [MFACode, setMFACode] = useState('');
  const [MFAMethod, setMFAMethod] = useState(defaultMFAMethod);
  const [isMFASent, setIsMFASent] = useState(defaultMFAMethod === EMAIL);
  const [isTrustedDevice, setIsTrustedDevice] = useState(false);
  const [showMFAMethods, setShowMFAMethods] = useState(false);

  useEffect(() => {
    if (loginAttemptResponse && loginAttemptResponse !== lastLoginAttemptResponseRef.current) {
      lastLoginAttemptResponseRef.current = loginAttemptResponse;
      if (loginAttemptResponse?.['is_mfa_required']) {
        const { is_mfa_authenticator_setup: isAuthenticatorSetup, is_mfa_email_setup: isEmailSetup } = loginAttemptResponse;
        if (isAuthenticatorSetup && isEmailSetup) {
          setShowMFAMethods(true);
        } else if (isEmailSetup) {
          setMFAMethod(EMAIL);
          _handleSendCode();
        } else {
          setMFAMethod(AUTH_APP);
        }
      }
    }
  }, [loginAttemptResponse]);

  const _handleSendCode = async () => {
    const res = await onSendCode();
    if (res.error) {
      message.error(Language.get('login', res.msg));
    } else {
      setIsMFASent(true);
      message.success(Language.get('login', 'MFA_CODE_SENT_SUCCESS'));
    }
  };

  const _handleVerifyCode = async () => {
    const res = await onVerifyCode({
      code: MFACode,
      'auth_type': MFAMethod,
      'trust_device': isTrustedDevice,
    });
    if (res.error) {
      message.error(Language.get('login', res.msg));
    } else {
      message.success(Language.get('login', 'MFA_CODE_VERIFIED_SUCCESS'));
      window.location.href = window.location.pathname;
    }
  };

  const renderMFAMethodSelector = () => (
    <Form.Item>
      <Select value={MFAMethod} onChange={setMFAMethod}>
        <Option value={AUTH_APP}>{Language.get('login', 'AUTH_APP')}</Option>
        <Option value={EMAIL}>{Language.get('login', 'EMAIL')}</Option>
      </Select>
      {MFAMethod === EMAIL && (
        <div className="mfa-actions">
          <Button
            size="small"
            type="primary"
            onClick={_handleSendCode}
            disabled={isMFASent}
          >
            {Language.get('login', 'SEND_MFA_CODE')}
          </Button>
        </div>
      )}
    </Form.Item>
  );

  const renderMFACodeInput = () => (
    <Form.Item>
      <Input
        maxLength={MAX_MFA_CODE_INPUT}
        placeholder={Language.get('login', 'ENTER_MFA_CODE')}
        value={MFACode}
        onChange={(e) => setMFACode(e.target.value)}
      />
      <div className="mfa-actions">
        <Form.Item name="remember" valuePropName="checked" noStyle>
          <Checkbox onChange={(e) => setIsTrustedDevice(e.target.checked)}>
            {Language.get('login', 'TRUST_DEVICE')}
          </Checkbox>
        </Form.Item>
        <Button size="small" type="primary" onClick={_handleVerifyCode}>
          {Language.get('login', 'VERIFY_MFA_CODE')}
        </Button>
      </div>
    </Form.Item>
  );

  return (
    <div>
      {showMFAMethods && renderMFAMethodSelector()}
      {(isMFASent || MFAMethod === AUTH_APP) && renderMFACodeInput()}
    </div>
  );
};

export default MFAVerification;
